import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { compact, dateShort, money0, pct, qty } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { BarChart, BarList, Donut, ErrorState, Kpi, Loading, Meter } from '../components/ui';

interface Dashboard {
  date: string;
  revenue: { fnb: number; rooms: number; total: number; bills: number; averageTicket: number };
  cost: { cogs: number; foodCostPct: number; targetFoodCostPct: number };
  rooms: { total: number; occupied: number; occupancyPct: number; dirty: number };
  operations: {
    openTickets: number;
    lowStockCount: number;
    pendingPurchaseOrders: number;
    headcount: number;
    monthlyWageBill: number;
  };
  trend: Array<{ day: string; fnb: number; rooms: number; bills: number }>;
  topItems: Array<{ name: string; quantity: number; revenue: number }>;
}

interface LowStockItem {
  id: string;
  code: string;
  name: string;
  unit: string;
  stock: number;
  reorderLevel: number;
  suggestedOrderQty: number;
}

export function DashboardPage() {
  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/api/dashboard'),
    refetchInterval: 60_000,
  });

  const lowStock = useQuery({
    queryKey: ['ingredients', 'low'],
    queryFn: () => api.get<LowStockItem[]>('/api/ingredients?lowOnly=true'),
  });

  if (dashboard.isLoading) return <Loading label="Loading today's position…" />;
  if (dashboard.error) return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;

  const data = dashboard.data!;
  const foodCostTone =
    data.cost.foodCostPct > data.cost.targetFoodCostPct + 5
      ? 'red'
      : data.cost.foodCostPct > data.cost.targetFoodCostPct
        ? 'amber'
        : '';

  return (
    <div className="stack">
      <PageHeader title="Today" subtitle={`Live position for ${dateShort(data.date)}`} />

      <div className="kpis">
        <Kpi
          label="Revenue today"
          value={money0(data.revenue.total)}
          foot={`F&B ${money0(data.revenue.fnb)} · Rooms ${money0(data.revenue.rooms)}`}
        />
        <Kpi
          label="Covers billed"
          value={data.revenue.bills}
          foot={`Average ticket ${money0(data.revenue.averageTicket)}`}
          tone="blue"
        />
        <Kpi
          label="Food cost"
          value={pct(data.cost.foodCostPct)}
          foot={`Target ${pct(data.cost.targetFoodCostPct)} · COGS ${money0(data.cost.cogs)}`}
          tone={foodCostTone}
        />
        <Kpi
          label="Occupancy"
          value={pct(data.rooms.occupancyPct)}
          foot={`${data.rooms.occupied} of ${data.rooms.total} rooms`}
          tone="blue"
        />
        <Kpi
          label="Below reorder"
          value={data.operations.lowStockCount}
          foot={`${data.operations.pendingPurchaseOrders} purchase order(s) in flight`}
          tone={data.operations.lowStockCount > 0 ? 'amber' : ''}
        />
        <Kpi
          label="Monthly wage bill"
          value={compact(data.operations.monthlyWageBill)}
          foot={`${data.operations.headcount} active employees`}
        />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Revenue, last 7 days</h2>
              <div className="small muted">Net of GST</div>
            </div>
            <div className="row gap-16 small">
              <span className="row gap-4">
                <i style={{ width: 9, height: 9, borderRadius: 3, background: 'var(--brand-500)', display: 'inline-block' }} />
                F&amp;B
              </span>
              <span className="row gap-4">
                <i style={{ width: 9, height: 9, borderRadius: 3, background: 'var(--accent)', display: 'inline-block' }} />
                Rooms
              </span>
            </div>
          </div>
          <div className="card-body">
            <BarChart
              stacked
              data={data.trend.map((day) => ({
                label: dateShort(day.day),
                value: day.fnb,
                stack: day.rooms,
              }))}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Right now</h2>
          </div>
          <div className="card-body row gap-16" style={{ alignItems: 'center' }}>
            <Donut value={data.rooms.occupancyPct} label="occupied" />
            <div className="col gap-6 grow">
              <div className="stat-line">
                <span className="dim">Open F&amp;B tickets</span>
                <b>{data.operations.openTickets}</b>
              </div>
              <div className="stat-line">
                <span className="dim">Rooms to clean</span>
                <b>{data.rooms.dirty}</b>
              </div>
              <div className="stat-line">
                <span className="dim">Purchase orders open</span>
                <b>{data.operations.pendingPurchaseOrders}</b>
              </div>
              <div className="row gap-6" style={{ marginTop: 8 }}>
                <Link className="btn sm primary" to="/pos">
                  Open POS
                </Link>
                <Link className="btn sm" to="/front-desk">
                  Room rack
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Reorder alerts</h2>
            <Link className="btn sm" to="/procurement">
              Raise a purchase order
            </Link>
          </div>
          {lowStock.isLoading ? (
            <Loading />
          ) : (lowStock.data?.length ?? 0) === 0 ? (
            <div className="empty">
              <span className="big">✓</span>Everything is above its reorder level
            </div>
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">On hand</th>
                    <th className="num">Reorder at</th>
                    <th style={{ width: 120 }}>Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.data!.slice(0, 8).map((item) => {
                    const cover = item.reorderLevel > 0 ? (item.stock / item.reorderLevel) * 100 : 0;
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="b">{item.name}</div>
                          <div className="xs muted mono">{item.code}</div>
                        </td>
                        <td className="num">{qty(item.stock, item.unit)}</td>
                        <td className="num dim">{qty(item.reorderLevel, item.unit)}</td>
                        <td>
                          <Meter value={cover} tone={cover < 50 ? 'danger' : 'warn'} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Top sellers, last 7 days</h2>
            <Link className="btn sm" to="/reports">
              Reports
            </Link>
          </div>
          <div className="card-body">
            <BarList
              rows={data.topItems.map((item) => ({
                label: `${item.name} · ${item.quantity} sold`,
                value: item.revenue,
              }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
